import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { rulesEndpoints } from '@/api/endpoints';
import { useDeskId } from '@/hooks/useDeskId';
import { Rule } from '@/shared/types/rules';
import { EventName } from '@/shared/types/segment';
import { NAME_SUFFIX_BUDGET, resolveAvailableName, truncateToServerLimit } from '@/util/checkNames';
import { LENGTH_XS } from '@/util/validation/constants';

import { API, onRuleCreated } from './useRules';

const resolveDuplicateName = (sourceName: string, deskId: string) => {
  const baseName = truncateToServerLimit(sourceName, LENGTH_XS - NAME_SUFFIX_BUDGET);
  return resolveAvailableName(
    rulesEndpoints.checkName(deskId, baseName, { desk_id: deskId }),
    baseName
  );
};

/** Copies a rule as Inactive. The server appends it to the end of the list. */
export const useDuplicateRule = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const deskId = useDeskId();

  const { mutate: duplicateRule, isPending: isDuplicating } = useMutation<Rule, Error, Rule>({
    meta: {
      segment: {
        success: EventName.RuleDuplicated,
        action: 'rule_duplicate',
        successProps: (rule: Rule) => ({ rule_id: rule.rule_id }),
      },
      errorToast: true,
    },
    mutationFn: async ({ name, description, triggers, condition, actions }) =>
      API.addRule(deskId, {
        name: await resolveDuplicateName(name, deskId),
        description,
        status: 'inactive',
        triggers,
        condition,
        actions,
      }),
    onSuccess: (copy) => {
      onRuleCreated(queryClient, copy, deskId);
      toast.success(t('rules.rule_duplicated', { 0: copy.name }));
    },
  });

  return { duplicateRule, isDuplicating };
};
